const isPlainObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const flattenRow = (row, prefix = '', target = {}) => {
  Object.keys(row).forEach(key => {
    const path = prefix ? `${prefix}.${key}` : key;
    const value = row[key];
    if (isPlainObject(value)) {
      flattenRow(value, path, target);
    } else if (Array.isArray(value)) {
      target[path] = JSON.stringify(value);
    } else {
      target[path] = value;
    }
  });
  return target;
};

const collectColumns = flatRows => {
  const columns = [];
  const seen = new Set();
  flatRows.forEach(row => {
    Object.keys(row).forEach(key => {
      if (!seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    });
  });
  return columns;
};

const escapeCell = value => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\r\n]/.test(str) || /^\s|\s$/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const rowsToCsv = rows => {
  const flatRows = rows.map(row => flattenRow(row));
  const columns = collectColumns(flatRows);
  const header = columns.map(escapeCell).join(',');
  const body = flatRows
    .map(row => columns.map(col => escapeCell(row[col])).join(','))
    .join('\r\n');
  return `${header}\r\n${body}`;
};

export const downloadCsv = (csv, filename) => {
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const EXPORT_MAX_ROWS = 20000;
export const EXPORT_PER_PAGE = 500;
export const EXPORT_PAGE_GAP_MS = 500;

export class ExportLimitError extends Error {
  constructor(total, maxRows) {
    super(`${total} rows exceed the ${maxRows} row limit. Narrow the filter first.`);
    this.name = 'ExportLimitError';
    this.total = total;
    this.maxRows = maxRows;
  }
}

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const unwrapPage = res => {
  const body = res && res.data ? res.data : {};
  return body.data !== undefined && body.data !== null && !Array.isArray(body.data) ? body.data : body;
};

export const fetchAllPages = async (client, url, params, options = {}) => {
  const {
    perPage = EXPORT_PER_PAGE,
    maxRows = EXPORT_MAX_ROWS,
    gapMs = EXPORT_PAGE_GAP_MS,
    onProgress
  } = options;
  let rows = [];
  let lastPage = 1;
  for (let page = 1; page <= lastPage; page++) {
    if (page > 1) await wait(gapMs);
    const res = await client.get(url, { params: { ...params, per_page: perPage, page } });
    const body = unwrapPage(res);
    const total = body.total !== undefined ? body.total : (body.meta && body.meta.total);
    if (page === 1 && maxRows && total > maxRows) {
      throw new ExportLimitError(total, maxRows);
    }
    lastPage = body.last_page || (body.meta && body.meta.last_page) || 1;
    rows = rows.concat(Array.isArray(body) ? body : (body.data || []));
    if (onProgress) onProgress(Math.round((page / lastPage) * 100), rows.length);
  }
  return rows;
};
